import React, { useState, useCallback, useEffect, useMemo } from 'react';
import ReactFlow, {
    MiniMap,
    Controls,
    Background,
    useNodesState,
    useEdgesState,
    addEdge,
    ConnectionLineType,
    Panel,
} from 'reactflow';
import 'reactflow/dist/style.css';
import TriggerNode from './TriggerNodes/TriggerNode';
import ActionNode from './TriggerNodes/ActionNode';
import NodeConfigSidebar from './NodeConfigSidebar';
import './TriggerBuilder.css';

// Memoized nodeTypes to prevent ReactFlow warnings
const nodeTypes = {
    trigger: TriggerNode,
    action: ActionNode,
};

const TriggerBuilder = ({ product, onSave }) => {
    const [triggerConfig, setTriggerConfig] = useState({
        nodes: [],
        edges: [],
        ...product.triggerConfig
    });

    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const [selectedNodeType, setSelectedNodeType] = useState('trigger');
    const [selectedNodes, setSelectedNodes] = useState([]);
    const [defaultFlowCreated, setDefaultFlowCreated] = useState(false);
    const [selectedNodeForConfig, setSelectedNodeForConfig] = useState(null);
    const [showSidebar, setShowSidebar] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveStatus, setSaveStatus] = useState(''); // 'saving', 'saved', 'error'
    const [triggerActive, setTriggerActive] = useState(false);

    const onConnect = useCallback(
        (params) => setEdges((eds) => addEdge(params, eds)),
        [setEdges]
    );

    const addNode = useCallback((type, actionType = null) => {
        const newNode = {
            id: `${type}-${Date.now()}`,
            type: type,
            position: { 
                x: Math.random() * 400 + 100, 
                y: Math.random() * 300 + 100 
            },
            data: getDefaultNodeData(type, actionType),
        };

        setNodes((nds) => nds.concat(newNode));
    }, [setNodes]);

    const updateNodeData = useCallback((nodeId, newData) => {
        setNodes((nds) =>
            nds.map((node) =>
                node.id === nodeId
                    ? { ...node, data: newData }
                    : node
            )
        );
    }, [setNodes]);

    const deleteSelectedNodes = useCallback(() => {
        if (selectedNodes.length === 0) {
            alert('Please select nodes to delete by clicking on them.');
            return;
        }

        if (window.confirm(`Are you sure you want to delete ${selectedNodes.length} selected node(s)?`)) {
            setNodes((nds) => nds.filter((node) => !selectedNodes.includes(node.id)));
            setEdges((eds) => eds.filter((edge) => 
                !selectedNodes.includes(edge.source) && !selectedNodes.includes(edge.target)
            ));
            setSelectedNodes([]);
        }
    }, [selectedNodes, setNodes, setEdges]);

    const clearAllNodes = useCallback(() => {
        if (nodes.length === 0) return;
        
        if (window.confirm('Are you sure you want to clear all nodes? This action cannot be undone.')) {
            setNodes([]);
            setEdges([]);
            setSelectedNodes([]);
            setDefaultFlowCreated(false); // Allow recreating default flow
        }
    }, [nodes.length, setNodes, setEdges]);

    const resetToDefaultFlow = useCallback(() => {
        if (window.confirm('Reset to default flow? This will replace your current flow.')) {
            createDefaultFlow();
        }
    }, []);

    const onSelectionChange = useCallback(({ nodes: selectedNodes }) => {
        setSelectedNodes(selectedNodes.map(node => node.id));
    }, []);

    const onNodeClick = useCallback((event, node) => {
        // Only open sidebar for action nodes (not trigger nodes)
        if (node.type === 'action') {
            setSelectedNodeForConfig(node);
            setShowSidebar(true);
        }
    }, []);

    const handleSidebarSave = useCallback((nodeId, newNodeData) => {
        updateNodeData(nodeId, newNodeData);
    }, [updateNodeData]);

    const handleSidebarClose = useCallback(() => {
        setShowSidebar(false);
        setSelectedNodeForConfig(null);
    }, []);

    // Validation logic
    const validateTriggerFlow = useCallback(() => {
        const actionNodes = nodes.filter(node => node.type === 'action');
        
        for (const node of actionNodes) {
            const config = node.data.config || {};
            
            switch (node.data.actionType) {
                case 'send_email':
                    if (!config.selectedTemplateId) {
                        return { isValid: false, error: `"${node.data.label}" needs an email template` };
                    }
                    break;
                case 'start_email_flow':
                    if (!config.selectedFlowId) {
                        return { isValid: false, error: `"${node.data.label}" needs an email flow` };
                    }
                    break;
                case 'send_webhook':
                    if (!config.webhookUrl) {
                        return { isValid: false, error: `"${node.data.label}" needs a webhook URL` };
                    }
                    break;
                case 'delay':
                    if (!config.delayAmount || config.delayAmount < 1) {
                        return { isValid: false, error: `"${node.data.label}" needs a valid delay duration` };
                    }
                    break;
                case 'schedule':
                    if (!config.scheduleTime) {
                        return { isValid: false, error: `"${node.data.label}" needs a schedule time` };
                    }
                    break;
            }
        }
        
        return { isValid: true, error: null };
    }, [nodes]);

    const validation = validateTriggerFlow();
    const canActivateTrigger = validation.isValid && nodes.length > 0;

    const getDefaultNodeData = (type, actionType = null) => {
        switch (type) {
            case 'trigger':
                // Dynamic trigger based on product payment type
                if (product.type === 'event') {
                    return {
                        label: 'Ticket Purchased',
                        triggerType: 'ticket_purchased',
                        config: { reason: 'Event product - triggers when any ticket is purchased' }
                    };
                } else if (product.paymentType === 'free') {
                    return {
                        label: 'Form Submitted (Freebie)',
                        triggerType: 'form_submitted',
                        config: { reason: 'Free product - triggers on form submission' }
                    };
                } else if (product.paymentType === 'stripe') {
                    return {
                        label: 'Purchase Completed',
                        triggerType: 'purchase_completed',
                        config: { reason: 'Paid product - triggers on successful payment' }
                    };
                } else if (product.paymentType === 'cash') {
                    return {
                        label: 'Manual Access Granted',
                        triggerType: 'access_granted',
                        config: { reason: 'Cash payment - triggers when admin grants access' }
                    };
                } else {
                    return {
                        label: 'Purchase Completed',
                        triggerType: 'purchase_completed',
                        config: {}
                    };
                }
            case 'action':
                // Use provided actionType or default to send_email
                const selectedActionType = actionType || 'send_email';
                const actionTypeInfo = actionTypes.find(at => at.id === selectedActionType) || actionTypes[0];
                
                return {
                    label: actionTypeInfo.label,
                    actionType: selectedActionType,
                    config: {}
                };
            default:
                return { label: 'Node' };
        }
    };

    const saveTriggerFlow = useCallback(async () => {
        // Validate before saving
        if (!validation.isValid) {
            alert(`Cannot save: ${validation.error}`);
            return;
        }

        setSaving(true);
        setSaveStatus('saving');
        
        try {
            const flowData = {
                triggerConfig: {
                    nodes: nodes,
                    edges: edges,
                    isActive: triggerActive,
                    lastUpdated: Date.now()
                }
            };
            
            await onSave(flowData);
            
            setSaveStatus('saved');
            setTimeout(() => setSaveStatus(''), 2000); // Clear status after 2 seconds
            
        } catch (error) {
            console.error('Error saving trigger flow:', error);
            setSaveStatus('error');
            setTimeout(() => setSaveStatus(''), 3000);
        } finally {
            setSaving(false);
        }
    }, [nodes, edges, triggerActive, validation, onSave]);

    // Available trigger types based on product type and payment configuration
    const getAvailableTriggers = () => {
        const triggers = [];
        
        if (product.type === 'event') {
            triggers.push({ 
                id: 'ticket_purchased', 
                label: 'Ticket Purchased', 
                icon: '🎫', 
                description: 'When customer purchases any event ticket' 
            });
        } else if (product.paymentType === 'stripe') {
            triggers.push({ 
                id: 'purchase_completed', 
                label: 'Purchase Completed', 
                icon: '🛒', 
                description: 'When customer completes Stripe payment' 
            });
        } else if (product.paymentType === 'free') {
            triggers.push({ 
                id: 'form_submitted', 
                label: 'Form Submitted (Freebie)', 
                icon: '🎁', 
                description: 'When customer submits form for free product' 
            });
        } else if (product.paymentType === 'cash') {
            triggers.push({ 
                id: 'access_granted', 
                label: 'Manual Access Granted', 
                icon: '🔓', 
                description: 'When admin manually grants access after cash payment' 
            });
        }
        
        return triggers;
    };

    const triggerTypes = getAvailableTriggers();

    // Available action types (cleaned up - only essential actions)
    const actionTypes = [
        { id: 'send_email', label: 'Send Email', icon: '📧', description: 'Send automated email' },
        { id: 'start_email_flow', label: 'Start Email Flow', icon: '⚡', description: 'Trigger automated email sequence' },
        { id: 'send_webhook', label: 'Send Webhook', icon: '🔗', description: 'Call external API' }
    ];

    // Timing/Delay actions
    const timingTypes = [
        { id: 'delay', label: 'Wait/Delay', icon: '⏰', description: 'Wait before next action' },
        { id: 'schedule', label: 'Schedule Action', icon: '📅', description: 'Schedule action for specific time' }
    ];

    // Load existing trigger config or create default flow
    useEffect(() => {
        if (product.triggerConfig && product.triggerConfig.nodes && product.triggerConfig.nodes.length > 0) {
            // Load existing config
            setNodes(product.triggerConfig.nodes);
            setEdges(product.triggerConfig.edges || []);
            setTriggerActive(product.triggerConfig.isActive || false);
            setDefaultFlowCreated(true);
        } else if (!defaultFlowCreated && (product.paymentType || product.type === 'event') && nodes.length === 0) {
            // Create default flow for regular products with payment type OR for events
            createDefaultFlow();
            setDefaultFlowCreated(true);
        }
    }, [product.triggerConfig, product.paymentType, product.type, nodes.length, defaultFlowCreated, setNodes, setEdges]);

    const createDefaultFlow = () => {
        // Create context-aware email labels
        const getEmailLabel = () => {
            if (product.type === 'event') {
                return 'Send Ticket Confirmation';
            } else if (product.paymentType === 'free') {
                return 'Send Freebie Email';
            } else if (product.paymentType === 'stripe') {
                return 'Send Purchase Confirmation';
            } else {
                return 'Send Welcome Email';
            }
        };

        const defaultNodes = [
            // Start Trigger
            {
                id: 'default-trigger',
                type: 'trigger',
                position: { x: 100, y: 200 },
                data: getDefaultNodeData('trigger'),
            },
            // Main Email Action (context-aware)
            {
                id: 'default-send-email',
                type: 'action',
                position: { x: 400, y: 200 },
                data: {
                    label: getEmailLabel(),
                    actionType: 'send_email',
                    config: {} // User needs to configure template
                },
            }
        ];

        const defaultEdges = [
            {
                id: 'trigger-to-email',
                source: 'default-trigger',
                target: 'default-send-email',
                sourceHandle: 'trigger-output',
                targetHandle: 'action-input',
                type: 'smoothstep',
            }
        ];

        // For events, add a delay and then send calendar invite
        if (product.type === 'event') {
            // Add delay node
            defaultNodes.push({
                id: 'default-delay',
                type: 'action',
                position: { x: 700, y: 200 },
                data: {
                    label: 'Wait 5 Minutes',
                    actionType: 'delay',
                    config: { delayAmount: 5, delayUnit: 'minutes' }
                },
            });

            // Add calendar invite after delay
            defaultNodes.push({
                id: 'default-calendar',
                type: 'action',
                position: { x: 1000, y: 200 },
                data: {
                    label: 'Send Calendar Invite',
                    actionType: 'schedule',
                    config: {}
                },
            });

            // Connect email → delay → calendar
            defaultEdges.push({
                id: 'email-to-delay',
                source: 'default-send-email',
                target: 'default-delay',
                sourceHandle: 'action-output',
                targetHandle: 'action-input',
                type: 'smoothstep',
            });

            defaultEdges.push({
                id: 'delay-to-calendar',
                source: 'default-delay',
                target: 'default-calendar',
                sourceHandle: 'action-output',
                targetHandle: 'action-input',
                type: 'smoothstep',
            });
        }

        setNodes(defaultNodes);
        setEdges(defaultEdges);
    };

    return (
        <div className="sales-trigger-builder">
            <div className="sales-trigger-header">
                <div className="sales-trigger-title">
                    <h4>Automation Triggers</h4>
                    <p className="sales-trigger-description">
                        Create automated workflows that trigger when customers interact with your product.
                    </p>
                </div>
                <div className="sales-trigger-actions">
                    {nodes.length === 0 && (product.paymentType || product.type === 'event') ? (
                        <button 
                            className="sally-trigger-btn primary"
                            onClick={resetToDefaultFlow}
                        >
                            <i className="fas fa-magic"></i>
                            Create Default Flow
                        </button>
                    ) : (
                        <>
                            {/* Compact Trigger Controls */}
                            <div className="sally-trigger-controls">
                                <div className="sally-activation-toggle">
                                    <label className="sally-toggle-switch">
                                        <input
                                            type="checkbox"
                                            checked={triggerActive}
                                            onChange={(e) => setTriggerActive(e.target.checked)}
                                            disabled={!canActivateTrigger}
                                        />
                                        <span className="sally-toggle-slider"></span>
                                    </label>
                                    <span className="sally-toggle-status">
                                        {triggerActive ? (
                                            <>
                                                <i className="fas fa-check-circle" style={{ color: '#10b981' }}></i>
                                                Active
                                            </>
                                        ) : (
                                            <>
                                                <i className="fas fa-pause-circle" style={{ color: '#f59e0b' }}></i>
                                                Inactive
                                            </>
                                        )}
                                    </span>
                                </div>
                                
                                <div className="sally-trigger-buttons">
                                    <button 
                                        className="sally-trigger-btn secondary"
                                        onClick={deleteSelectedNodes}
                                        disabled={selectedNodes.length === 0}
                                        title={`Delete ${selectedNodes.length} selected node(s)`}
                                    >
                                        <i className="fas fa-trash"></i>
                                        {selectedNodes.length > 0 && <span>({selectedNodes.length})</span>}
                                    </button>
                                    <button 
                                        className="sally-trigger-btn danger"
                                        onClick={clearAllNodes}
                                        disabled={nodes.length === 0}
                                        title="Clear all nodes"
                                    >
                                        <i className="fas fa-broom"></i>
                                    </button>
                                </div>
                            </div>
                            
                            {!validation.isValid && (
                                <div className="sally-validation-error">
                                    <i className="fas fa-exclamation-triangle"></i>
                                    {validation.error}
                                </div>
                            )}
                        </>
                    )}
                    
                    <button 
                        className={`sales-save-flow-btn ${saveStatus}`}
                        onClick={saveTriggerFlow}
                        disabled={saving || !validation.isValid}
                        title={!validation.isValid ? validation.error : 'Save trigger configuration'}
                    >
                        {saving && '⏳ Saving...'}
                        {saveStatus === 'saved' && '✅ Saved!'}
                        {saveStatus === 'error' && '❌ Error'}
                        {!saving && saveStatus !== 'saved' && saveStatus !== 'error' && '💾 Save Flow'}
                    </button>
                </div>
            </div>

            <div className="sales-flow-container">
                <div className="sales-node-palette">
                    <h5>Add Nodes</h5>
                    
                    <div className="sales-node-category">
                        <h6>🚀 Current Flow</h6>
                        <div className="sales-current-trigger-info">
                            <div className="sales-trigger-badge">
                                {product.type === 'event' && '🎫 Ticket Purchased'}
                                {product.type !== 'event' && product.paymentType === 'stripe' && '🛒 Purchase Completed'}
                                {product.type !== 'event' && product.paymentType === 'free' && '🎁 Form Submitted (Freebie)'}
                                {product.type !== 'event' && product.paymentType === 'cash' && '🔓 Manual Access Granted'}
                                {product.type !== 'event' && !product.paymentType && '⚠️ Configure Payment Type First'}
                            </div>
                            <p className="sales-trigger-explanation">
                                {product.type === 'event' && 'Default flow auto-created for event tickets:'}
                                {product.type !== 'event' && product.paymentType === 'free' && 'Default flow auto-created for freebie:'}
                                {product.type !== 'event' && product.paymentType === 'stripe' && 'Default flow auto-created for purchases:'}
                                {product.type !== 'event' && product.paymentType === 'cash' && 'Default flow auto-created for manual access:'}
                                {product.type !== 'event' && !product.paymentType && 'Go to Payment Settings to configure payment type first'}
                            </p>
                            {(product.type === 'event' || product.paymentType) && (
                                <div className="sales-default-flow-info">
                                    {product.type === 'event' && (
                                        <>
                                            <div className="sales-flow-step">✅ Send Ticket Confirmation Email</div>
                                            <div className="sales-flow-step">✅ Wait 5 Minutes</div>
                                            <div className="sales-flow-step">✅ Send Calendar Invite</div>
                                        </>
                                    )}
                                    {product.type !== 'event' && product.paymentType === 'free' && (
                                        <div className="sales-flow-step">✅ Send Freebie Email</div>
                                    )}
                                    {product.type !== 'event' && product.paymentType === 'stripe' && (
                                        <div className="sales-flow-step">✅ Send Purchase Confirmation</div>
                                    )}
                                    {product.type !== 'event' && product.paymentType === 'cash' && (
                                        <div className="sales-flow-step">✅ Send Welcome Email</div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="sales-node-category">
                        <h6>⚡ Additional Actions</h6>
                        {actionTypes.map(action => (
                            <button
                                key={action.id}
                                className="sales-node-btn action"
                                onClick={() => addNode('action', action.id)}
                                title={action.description}
                            >
                                <span className="sales-node-icon">{action.icon}</span>
                                <span className="sales-node-label">{action.label}</span>
                            </button>
                        ))}
                    </div>

                    <div className="sales-node-category">
                        <h6>⏰ Timing</h6>
                        {timingTypes.map(timing => (
                            <button
                                key={timing.id}
                                className="sales-node-btn timing"
                                onClick={() => addNode('action', timing.id)}
                                title={timing.description}
                            >
                                <span className="sales-node-icon">{timing.icon}</span>
                                <span className="sales-node-label">{timing.label}</span>
                            </button>
                        ))}
                    </div>
                </div>

                <div className="sales-flow-editor">
                    <ReactFlow
                        nodes={nodes}
                        edges={edges}
                        onNodesChange={onNodesChange}
                        onEdgesChange={onEdgesChange}
                        onConnect={onConnect}
                        onSelectionChange={onSelectionChange}
                        onNodeClick={onNodeClick}
                        nodeTypes={nodeTypes}
                        connectionLineType={ConnectionLineType.SmoothStep}
                        fitView
                        className="sales-react-flow"
                        multiSelectionKeyCode="Shift"
                        deleteKeyCode="Delete"
                    >
                        <Controls className="sales-flow-controls" />
                        <MiniMap className="sales-flow-minimap" />
                        <Background variant="dots" gap={20} size={1} />
                        
                        <Panel position="top-center" className="sales-flow-panel">
                            <div className="sales-flow-info">
                                <span className="sales-flow-status">
                                    {nodes.length} nodes, {edges.length} connections
                                </span>
                                {selectedNodes.length > 0 && (
                                    <span className="sales-selection-info">
                                        • {selectedNodes.length} selected
                                    </span>
                                )}
                            </div>
                        </Panel>

                        <Panel position="bottom-left" className="sales-help-panel">
                            <div className="sales-help-info">
                                <div className="sales-help-item">
                                    <strong>💡 Quick Tips:</strong>
                                </div>
                                <div className="sales-help-item">
                                    • Click action nodes to configure
                                </div>
                                <div className="sales-help-item">
                                    • Hold Shift for multi-select
                                </div>
                                <div className="sales-help-item">
                                    • Press Delete key to remove
                                </div>
                                <div className="sales-help-item">
                                    • Drag from handles to connect
                                </div>
                            </div>
                        </Panel>
                    </ReactFlow>

                    {/* Node Configuration Sidebar - positioned within flow editor */}
                    <NodeConfigSidebar
                        isOpen={showSidebar}
                        selectedNode={selectedNodeForConfig}
                        onClose={handleSidebarClose}
                        onSave={handleSidebarSave}
                    />
                </div>
            </div>

            {/* Quick Start Templates */}
            <div className="sales-trigger-templates">
                <h5>Quick Start Templates</h5>
                <div className="sales-templates-grid">
                    <div className="sales-template-card" onClick={() => loadTemplate('welcome_sequence')}>
                        <div className="sales-template-icon">📧</div>
                        <h6>Welcome Sequence</h6>
                        <p>Send welcome email + add to newsletter</p>
                    </div>
                    <div className="sales-template-card" onClick={() => loadTemplate('payment_follow_up')}>
                        <div className="sales-template-icon">💳</div>
                        <h6>Payment Follow-up</h6>
                        <p>Handle failed payments + retries</p>
                    </div>
                    <div className="sales-template-card" onClick={() => loadTemplate('customer_onboarding')}>
                        <div className="sales-template-icon">🎯</div>
                        <h6>Customer Onboarding</h6>
                        <p>Complete onboarding sequence</p>
                    </div>
                </div>
            </div>

        </div>
    );

    function loadTemplate(templateId) {
        // TODO: Load predefined templates
        console.log('Loading template:', templateId);
        alert('Templates coming soon!');
    }
};

export default TriggerBuilder;
